// components/RoleSelectionModal.tsx
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import {
  GraduationCap,
  Users,
  BookOpen,
  Shield,
  School,
} from "lucide-react";

import { getAuth } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase/config";

interface RoleSelectionModalProps {
  isOpen: boolean;
  onRoleSelect: (role: string) => void;
  onClose: () => void;
}

const roles = [
  {
    id: "admin",
    title: "School Administrator",
    description: "Full access to all system features and management tools",
    icon: Shield,
    color: "bg-red-500", // #EF4444
  },
  {
    id: "school_staff",
    title: "School Staff",
    description: "Access to teaching tools, grade management, and student tracking",
    icon: GraduationCap,
    color: "bg-blue-500", // #3B82F6
  },
  {
    id: "parent",
    title: "Parent/Guardian",
    description: "Monitor your child's progress, attendance, and school communications",
    icon: Users,
    color: "bg-green-500", // #22C55E
  },
  {
    id: "student",
    title: "Student",
    description: "Access your grades, assignments, schedule, and school resources",
    icon: BookOpen,
    color: "bg-purple-500", // #A855F7
  },
];

const RoleSelectionModal = ({
  isOpen,
  onRoleSelect,
  onClose,
}: RoleSelectionModalProps) => {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    if (!selectedRole) {
      alert("Please select a role before continuing.");
      return;
    }

    try {
      setSaving(true);

      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        alert("Unable to retrieve user information. Please log in again.");
        setSaving(false);
        return;
      }

      const uid = currentUser.uid;
      const userRef = doc(db, "users", uid);

      await updateDoc(userRef, { role: selectedRole });
      localStorage.setItem("userRole", selectedRole);

      onRoleSelect(selectedRole);

      alert(`Switched to ${selectedRole} successfully!`);
      onClose();

      window.location.href = "/";
    } catch (error) {
      console.error("Error switching role: ", error);
      alert("Failed to switch role. Please try again.");
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#1A2A44] text-white border-none">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold flex items-center justify-center gap-2">
            <School className="h-6 w-6 text-blue-600" /> {/* #2563EB */}
            How do you plan to use Sonoma Cloud?
          </DialogTitle>
          <p className="text-center text-gray-200 mt-2">
            Select your role to access the appropriate features and dashboard
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {roles.map((role) => (
            <Card
              key={role.id}
              onClick={() => setSelectedRole(role.id)}
              className={`cursor-pointer transition-all duration-200 hover:shadow-lg bg-[#2A4066] border border-gray-700 rounded-lg ${
                selectedRole === role.id ? "ring-2 ring-blue-500" : "" // #3B82F6
              }`}
            >
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-12 h-12 ${role.color} rounded-full flex items-center justify-center`}
                  >
                    <role.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-white">
                      {role.title}
                    </CardTitle>
                    <CardDescription className="text-sm mt-1 text-gray-300">
                      {role.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>

        <div className="flex justify-center mt-6">
          <Button
            onClick={handleContinue}
            disabled={!selectedRole || saving}
            className="px-8 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-500" // #3B82F6, #2563EB
          >
            {saving ? "Switching..." : "Continue to Dashboard"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RoleSelectionModal;